/**
 * Shared Kiosk Token Manager
 * 
 * Centralized token caching with sessionStorage persistence so the token
 * survives page navigations, re-renders, and React component re-mounts.
 * Both AdminBookingsPage and AdminTransactionsPage use this single module
 * to avoid duplicate token requests and 429 rate-limit errors.
 */

const KIOSK_API_BASE = process.env.REACT_APP_KIOSK_API_BASE || 'https://pickleball-kiosk.plgucamsur.com/api';
const KIOSK_TOKEN_URL = `${KIOSK_API_BASE}/kiosk/token`;
const KIOSK_TRANSACTIONS_URL = `${KIOSK_API_BASE}/transactions`;

const STORAGE_KEY = 'kiosk_token_cache';

// In-memory promise to deduplicate concurrent fetches within the same JS context
let _tokenPromise = null;

/**
 * Read cached token from sessionStorage.
 * Returns { token, expiresAt } or null.
 */
const readCache = () => {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed && parsed.token && parsed.expiresAt) return parsed;
  } catch {
    // corrupt data – ignore
  }
  return null;
};

/**
 * Write token to sessionStorage.
 */
const writeCache = (token, expiresAt) => {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ token, expiresAt }));
  } catch {
    // storage full or unavailable – cache will only live in memory
  }
};

/**
 * Clear cached token (useful on logout or forced refresh).
 */
export const clearKioskTokenCache = () => {
  try { sessionStorage.removeItem(STORAGE_KEY); } catch { /* ignore */ }
  _tokenPromise = null;
};

/**
 * Get a valid kiosk token, reusing the cached one when possible.
 * - Checks sessionStorage first (survives navigation / refresh).
 * - Deduplicates concurrent calls within the same JS tick.
 * - Returns null (never throws) so callers can gracefully skip kiosk features.
 */
export const getKioskToken = async () => {
  const now = Date.now();

  // 1. Check sessionStorage cache (with 60s safety buffer)
  const cached = readCache();
  if (cached && cached.expiresAt > now + 60000) {
    return cached.token;
  }

  // 2. If a fetch is already in-flight, wait for it
  if (_tokenPromise) {
    return _tokenPromise;
  }

  // 3. Start a new fetch
  _tokenPromise = (async () => {
    try {
      const res = await fetch(KIOSK_TOKEN_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify({ pos_id: 'cwc' }),
        mode: 'cors',
      });

      if (!res.ok) {
        const errorText = await res.text().catch(() => '');
        console.error('Kiosk token request failed:', res.status, errorText);
        return null;
      }

      const data = await res.json();
      const token = data?.token;
      const expiresIn = data?.expires_in || 86400; // default 24h

      if (!token) {
        console.error('No token in kiosk response:', data);
        return null;
      }

      // Cache with 60s early-expiry buffer
      const expiresAt = now + expiresIn * 1000 - 60000;
      writeCache(token, expiresAt);

      console.log('Kiosk token cached, expires in:', expiresIn, 'seconds');
      return token;
    } catch (err) {
      console.error('Failed to get kiosk token:', err);
      return null;
    } finally {
      _tokenPromise = null;
    }
  })();

  return _tokenPromise;
};

/**
 * Fetch kiosk transactions for the calendar / bookings page.
 * Returns the array of transactions or [] on failure.
 */
export const fetchKioskTransactionsData = async () => {
  try {
    const token = await getKioskToken();
    if (!token) return [];

    const res = await fetch(KIOSK_TRANSACTIONS_URL, {
      headers: { 'X-KIOSK-TOKEN': token },
    });

    if (!res.ok) {
      console.error('Failed to fetch kiosk transactions:', res.status);
      return [];
    }

    const responseData = await res.json();
    return Array.isArray(responseData?.data) ? responseData.data : [];
  } catch (error) {
    console.error('Failed to fetch kiosk transactions:', error);
    return [];
  }
};

export { KIOSK_API_BASE, KIOSK_TRANSACTIONS_URL };
